// "Start time must be before the end time." — the one place <TimeRangeFields>
// still changes a clock time the user already filled, and it asks first.
//
// Moving a start past its end leaves the span inverted, which no host form will
// submit. The triad used to repair it silently by dragging the end along to
// preserve the duration; that is a good guess (it is exactly what the New
// travel return leg wants) but it is still a rewrite of an entered value, so it
// is now one of four choices rather than a fait accompli:
//
//   move-end  shift the end by the same amount the start moved
//   end       pick a new end                  duration re-derives from the span
//   start     pick a different start          duration re-derives from the span
//   hours     pick a new duration             end = start + that duration
//
// "Move the end" SHIFTS rather than rebuilding from the duration. The two agree
// whenever the span already equals the snapped hours — every seeded-leg case
// this exists for — and differ only on a timer or overridden row, where
// rebuilding would round a recorded 2h 28m to 2h 30m. Snapping a clock time is
// the thing this whole component stopped doing. The choice names the resulting
// time either way, so nothing is hidden.
//
// The start the user typed is ALREADY committed to the form when this opens —
// it is a fact like any other, and hiding it while asking about it would be
// confusing. Cancelling is therefore an undo: the caller puts `prevStart` back.
//
// Mounted only while a conflict exists, so every open starts from fresh state.

import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { DateInput } from './DateInput';
import { DurationStepper } from './DurationStepper';
import { endFromDuration, hoursFromRange } from '../utils/duration';
import { fmtDateTime } from '../utils/date';
import './SpanConflictModal.css';

/** The four ways out, in the order they are offered. */
type Mode = 'move-end' | 'end' | 'start' | 'hours';

export interface SpanConflictResolution {
  start: number;
  end: number;
  hours: string;
}

export interface SpanConflictModalProps {
  /** The start just entered — at or after `end`. Already in the form. */
  start: number;
  /** The end as it stands, untouched. */
  end: number;
  /** The duration as it stands; '' when none has been entered. */
  hours: string;
  /** The start before this edit, restored by Cancel. */
  prevStart: number | null;
  onResolve: (next: SpanConflictResolution) => void;
  onCancel: () => void;
}

function isUsableHours(h: string): boolean {
  const n = Number(h);
  return h.trim() !== '' && Number.isFinite(n) && n > 0;
}

export function SpanConflictModal(props: SpanConflictModalProps): JSX.Element {
  const {
    start, end, hours, prevStart, onResolve, onCancel,
  } = props;

  // Shift the end by exactly what the start moved, so the recorded span
  // survives. With no previous start to measure against, fall back to the
  // duration; with neither, there is nothing to drag and the choice is
  // not offered.
  const draggedEnd = prevStart !== null
    ? end + (start - prevStart)
    : (isUsableHours(hours) ? endFromDuration(start, hours) : null);
  const canMoveEnd = draggedEnd !== null && draggedEnd > start;

  const [mode, setMode] = useState<Mode>(canMoveEnd ? 'move-end' : 'end');
  // Seeded to the dragged end so the picker opens on the answer most people
  // want, then lets them nudge it.
  const [endPick, setEndPick] = useState<number | null>(draggedEnd ?? start);
  // Seeded to the PREVIOUS start: choosing this row means "that edit was
  // wrong", and the previous value is the only one known to be valid.
  const [startPick, setStartPick] = useState<number | null>(prevStart);
  const [hoursPick, setHoursPick] = useState(isUsableHours(hours) ? hours : '0.10');

  // Three of the four host forms render <TimeRangeFields> inside a <Modal> of
  // their own, and <Modal> listens for Escape on `document` in the bubble
  // phase. Without this, one Escape dismissed this dialog AND closed the trip
  // or edit modal behind it, discarding a half-filled form. Capture runs ahead
  // of every bubble listener, including this dialog's own.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const resolution: SpanConflictResolution | null = (() => {
    switch (mode) {
      case 'move-end':
        return canMoveEnd ? { start, end: draggedEnd!, hours } : null;
      case 'end':
        return endPick === null || endPick <= start
          ? null
          : { start, end: endPick, hours: hoursFromRange(start, endPick) };
      case 'start':
        if (startPick === null || startPick >= end) return null;
        // Putting the start back where it was is an UNDO, not an edit, so the
        // triad is restored verbatim — including a duration that was
        // deliberately overridden. Re-deriving it from the span would quietly
        // rewrite a timer row's billed 2.50 down to its recorded 2h 28m.
        return startPick === prevStart
          ? { start: startPick, end, hours }
          : { start: startPick, end, hours: hoursFromRange(startPick, end) };
      case 'hours':
        return isUsableHours(hoursPick)
          ? { start, end: endFromDuration(start, hoursPick), hours: hoursPick }
          : null;
      default:
        return null;
    }
  })();

  const option = (
    value: Mode,
    label: string,
    detail: string,
    control?: { id: string; label: string; node: JSX.Element },
  ): JSX.Element => (
    <div className={mode === value ? 'scm-option scm-option--on' : 'scm-option'}>
      <label className="scm-option-head">
        <input
          type="radio"
          name="scm-mode"
          value={value}
          checked={mode === value}
          onChange={() => setMode(value)}
        />
        <span className="scm-option-label">{label}</span>
      </label>
      <p className="scm-option-detail">{detail}</p>
      {mode === value && control && (
        <div className="scm-option-body">
          {/* Its own label, and deliberately NOT "End time" / "Duration" —
              those belong to the triad behind this dialog, and a duplicate
              would make either one ambiguous to a screen reader. */}
          <label className="scm-control-label" htmlFor={control.id}>{control.label}</label>
          {control.node}
        </div>
      )}
    </div>
  );

  return (
    // Wrapper exists only to lift this overlay above the host form's own
    // modal — <Modal> hardcodes `z-index: var(--z-modal)` on both.
    <div className="scm-nested">
      <Modal
        title="Start time must be before the end time"
        open
        onClose={onCancel}
        footer={(
          <>
            <button type="button" className="btn-charcoal-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="btn-cyan-primary"
              onClick={() => resolution !== null && onResolve(resolution)}
              disabled={resolution === null}
            >
              Apply
            </button>
          </>
        )}
      >
        <p className="scm-lede">
          You entered a start of <strong>{fmtDateTime(start)}</strong>, which is
          {start === end ? ' the same as ' : ' after '}
          the end time of <strong>{fmtDateTime(end)}</strong>. How would you
          like to fix it?
        </p>

        <div className="scm-options">
          {canMoveEnd && option(
            'move-end',
            'Move the end time',
            `Keeps the length of the entry — the end becomes ${fmtDateTime(draggedEnd!)}.`,
          )}

          {option(
            'end',
            'Change the end time',
            'The duration is recalculated from the new span.',
            {
              id: 'scm-end',
              label: 'New end time',
              node: <DateInput id="scm-end" value={endPick} onChange={setEndPick} withTime />,
            },
          )}

          {option(
            'start',
            'Change the start time',
            prevStart === null
              ? 'Pick a start that falls before the end time.'
              : `Leave it at ${fmtDateTime(prevStart)} to undo the change.`,
            {
              id: 'scm-start',
              label: 'New start time',
              node: <DateInput id="scm-start" value={startPick} onChange={setStartPick} withTime />,
            },
          )}

          {option(
            'hours',
            'Change the duration',
            'Keeps the start you entered; the end becomes the start plus this duration.',
            {
              id: 'scm-hours',
              label: 'New duration',
              node: <DurationStepper id="scm-hours" value={hoursPick} onChange={setHoursPick} />,
            },
          )}
        </div>

        {resolution === null && (
          <p className="scm-warn" role="alert">
            That still leaves the end time at or before the start time.
          </p>
        )}
      </Modal>
    </div>
  );
}
